import React, { useEffect, useState } from 'react';
import {
  X,
  Volume2,
  Bell,
  Moon,
  Save,
  Check,
  Sliders,
  Hash,
  Lock
} from 'lucide-react';
import { api } from '../../lib/api';
import { useChat } from '../../context/ChatContext';
import type { NotificationLevel, DmNotificationLevel } from '@sahaj/types';

interface NotificationSettingsModalProps {
  workspaceId: string;
  isOpen: boolean;
  onClose: () => void;
}

export const NotificationSettingsModal: React.FC<NotificationSettingsModalProps> = ({
  workspaceId,
  isOpen,
  onClose,
}) => {
  const { channels } = useChat();

  const [soundEnabled, setSoundEnabled] = useState(true);
  const [desktopPushEnabled, setDesktopPushEnabled] = useState(true);
  const [dmsLevel, setDmsLevel] = useState<DmNotificationLevel>('ALL');
  const [channelsDefault, setChannelsDefault] = useState<NotificationLevel>('MENTIONS');
  const [quietHoursEnabled, setQuietHoursEnabled] = useState(false);
  const [quietHoursStart, setQuietHoursStart] = useState('22:00');
  const [quietHoursEnd, setQuietHoursEnd] = useState('08:00');

  // Channel-specific overrides map: channelId -> level
  const [channelOverrides, setChannelOverrides] = useState<Record<string, NotificationLevel>>({});

  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [permissionState, setPermissionState] = useState<NotificationPermission>('default');

  useEffect(() => {
    if (typeof window !== 'undefined' && 'Notification' in window) {
      setPermissionState(Notification.permission);
    }
  }, []);

  useEffect(() => {
    if (!isOpen || !workspaceId) return;
    const loadPrefsAndChannelSettings = async () => {
      try {
        setIsLoading(true);
        const data = await api.notifications.getPreferences(workspaceId);
        setSoundEnabled(data.soundEnabled);
        setDesktopPushEnabled(data.desktopPushEnabled);
        setDmsLevel(data.dmsLevel);
        setChannelsDefault(data.channelsDefault);
        setQuietHoursEnabled(data.quietHoursEnabled);
        if (data.quietHoursStart) setQuietHoursStart(data.quietHoursStart.slice(0, 5));
        if (data.quietHoursEnd) setQuietHoursEnd(data.quietHoursEnd.slice(0, 5));

        // Load channel-specific settings
        const overrides: Record<string, NotificationLevel> = {};
        for (const ch of channels) {
          try {
            const setting = await api.notifications.getChannelSetting(ch.id);
            if (setting && setting.level) {
              overrides[ch.id] = setting.level;
            }
          } catch (ignored) {}
        }
        setChannelOverrides(overrides);
      } catch (err) {
        console.error('Failed to load notification preferences', err);
      } finally {
        setIsLoading(false);
      }
    };
    loadPrefsAndChannelSettings();
  }, [isOpen, workspaceId, channels]);

  const handleRequestDesktopPermission = async () => {
    if (typeof window !== 'undefined' && 'Notification' in window) {
      const perm = await Notification.requestPermission();
      setPermissionState(perm);
      if (perm === 'granted') {
        setDesktopPushEnabled(true);
      }
    }
  };

  const handleChannelSettingChange = async (channelId: string, level: NotificationLevel) => {
    setChannelOverrides(prev => ({ ...prev, [channelId]: level }));
    try {
      await api.notifications.updateChannelSetting(channelId, { level });
    } catch (err) {
      console.error('Failed to update channel notification setting', err);
    }
  };

  const handleSave = async () => {
    if (!workspaceId) return;
    try {
      setIsSaving(true);
      await api.notifications.updatePreferences(workspaceId, {
        soundEnabled,
        desktopPushEnabled,
        dmsLevel,
        channelsDefault,
        quietHoursEnabled,
        quietHoursStart: quietHoursStart + ':00',
        quietHoursEnd: quietHoursEnd + ':00',
      });
      setSaveSuccess(true);
      setTimeout(() => {
        setSaveSuccess(false);
        onClose();
      }, 1000);
    } catch (err) {
      console.error('Failed to update notification preferences', err);
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-in fade-in duration-150">
      <div className="w-full max-w-lg bg-card border border-border rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="px-6 py-4 border-b border-border flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-xl bg-primary/10 flex items-center justify-center text-primary">
              <Sliders className="h-5 w-5" />
            </div>
            <div>
              <h2 className="text-base font-bold text-foreground">Notification Preferences</h2>
              <p className="text-xs text-muted-foreground">Manage sound, alerts, and per-channel overrides</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-xl hover:bg-muted text-muted-foreground hover:text-foreground transition"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto space-y-6 text-sm">
          {isLoading ? (
            <div className="flex items-center justify-center py-12 text-muted-foreground">
              <div className="animate-spin rounded-full h-7 w-7 border-b-2 border-primary" />
            </div>
          ) : (
            <>
              {/* Sound & Audio */}
              <div className="space-y-3">
                <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-2">
                  <Volume2 className="h-4 w-4" /> Sound & Audio
                </h3>
                <div className="flex items-center justify-between p-3.5 bg-muted/30 rounded-xl border border-border/60">
                  <div>
                    <span className="font-medium text-foreground block">Notification Chime</span>
                    <span className="text-xs text-muted-foreground">
                      Play audible chime for incoming messages and mentions
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() => setSoundEnabled(!soundEnabled)}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      soundEnabled ? 'bg-primary' : 'bg-muted-foreground/30'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        soundEnabled ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>
              </div>

              {/* Desktop Alerts */}
              <div className="space-y-3">
                <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-2">
                  <Bell className="h-4 w-4" /> Desktop Notifications
                </h3>
                <div className="p-3.5 bg-muted/30 rounded-xl border border-border/60 space-y-2.5">
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="font-medium text-foreground block">Browser Banners</span>
                      <span className="text-xs text-muted-foreground">
                        Show system popups when Sahaj Connect is in background
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => setDesktopPushEnabled(!desktopPushEnabled)}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        desktopPushEnabled ? 'bg-primary' : 'bg-muted-foreground/30'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          desktopPushEnabled ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>

                  {permissionState !== 'granted' && (
                    <div className="pt-2 border-t border-border/40 flex items-center justify-between">
                      <span className="text-xs text-amber-500 font-medium">Browser permission needed</span>
                      <button
                        type="button"
                        onClick={handleRequestDesktopPermission}
                        className="px-2.5 py-1 text-xs bg-primary/10 text-primary hover:bg-primary/20 rounded-lg font-semibold transition"
                      >
                        Enable in Browser
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Default Channel Notification Tiers */}
              <div className="space-y-3">
                <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Default Channel Notification Level
                </h3>
                <div className="grid grid-cols-3 gap-2.5">
                  {[
                    { id: 'ALL', label: 'All Activity', desc: 'Every message' },
                    { id: 'MENTIONS', label: 'Mentions Only', desc: '@mentions & replies' },
                    { id: 'MUTED', label: 'Muted', desc: 'Badge only' },
                  ].map((tier) => (
                    <button
                      key={tier.id}
                      type="button"
                      onClick={() => setChannelsDefault(tier.id as NotificationLevel)}
                      className={`p-3 rounded-xl border text-left transition flex flex-col justify-between ${
                        channelsDefault === tier.id
                          ? 'border-primary bg-primary/10 text-primary'
                          : 'border-border bg-card hover:bg-muted text-foreground'
                      }`}
                    >
                      <span className="font-semibold text-xs">{tier.label}</span>
                      <span className="text-[10px] text-muted-foreground mt-1">{tier.desc}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Per-Channel Overrides */}
              {channels.length > 0 && (
                <div className="space-y-3">
                  <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    Per-Channel Overrides
                  </h3>
                  <div className="space-y-2 max-h-44 overflow-y-auto pr-1">
                    {channels.map((ch) => {
                      const currentLevel = channelOverrides[ch.id] || channelsDefault;
                      return (
                        <div
                          key={ch.id}
                          className="flex items-center justify-between p-2.5 bg-muted/20 rounded-xl border border-border/50"
                        >
                          <div className="flex items-center gap-2 min-w-0">
                            {ch.isPrivate ? (
                              <Lock className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                            ) : (
                              <Hash className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                            )}
                            <span className="font-medium text-xs text-foreground truncate">
                              {ch.name}
                            </span>
                          </div>

                          <select
                            value={currentLevel}
                            onChange={(e) => handleChannelSettingChange(ch.id, e.target.value as NotificationLevel)}
                            className="bg-background border border-border rounded-lg px-2 py-1 text-xs text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                          >
                            <option value="ALL">All Messages</option>
                            <option value="MENTIONS">Mentions Only</option>
                            <option value="MUTED">Muted</option>
                          </select>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Quiet Hours */}
              <div className="space-y-3">
                <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-2">
                  <Moon className="h-4 w-4" /> Quiet Hours
                </h3>
                <div className="p-3.5 bg-muted/30 rounded-xl border border-border/60 space-y-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="font-medium text-foreground block">Mute During Scheduled Hours</span>
                      <span className="text-xs text-muted-foreground">
                        Automatically suppress notification sound between selected times
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => setQuietHoursEnabled(!quietHoursEnabled)}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        quietHoursEnabled ? 'bg-primary' : 'bg-muted-foreground/30'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          quietHoursEnabled ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>

                  {quietHoursEnabled && (
                    <div className="flex items-center gap-3 pt-2 border-t border-border/40 text-xs">
                      <div className="flex-1">
                        <label className="text-[11px] text-muted-foreground font-medium mb-1 block">From</label>
                        <input
                          type="time"
                          value={quietHoursStart}
                          onChange={(e) => setQuietHoursStart(e.target.value)}
                          className="w-full bg-background border border-border rounded-lg p-2 text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                        />
                      </div>
                      <div className="flex-1">
                        <label className="text-[11px] text-muted-foreground font-medium mb-1 block">To</label>
                        <input
                          type="time"
                          value={quietHoursEnd}
                          onChange={(e) => setQuietHoursEnd(e.target.value)}
                          className="w-full bg-background border border-border rounded-lg p-2 text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-border bg-card/40 flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-xs font-semibold text-muted-foreground hover:text-foreground hover:bg-muted rounded-xl transition"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={isSaving}
            className="inline-flex items-center gap-1.5 px-4 py-2 bg-primary text-primary-foreground text-xs font-semibold rounded-xl hover:bg-primary/90 transition shadow-sm disabled:opacity-50"
          >
            {saveSuccess ? (
              <>
                <Check className="h-4 w-4" /> Saved!
              </>
            ) : isSaving ? (
              <>
                <div className="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-white" /> Saving...
              </>
            ) : (
              <>
                <Save className="h-4 w-4" /> Save Preferences
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};
